// components/models/Seedance2ControlPanel.tsx
import React, { useCallback, useMemo } from 'react';
import AutoGrowingTextarea from '../AutoGrowingTextarea';
import RangeControl from '../ui/RangeControl';
import Select from '../ui/Select';
import Switch from '../ui/Switch';
import VideoInputRangeControls from './VideoInputRangeControls';
import { LockableFile, Seedance2AspectRatio, Seedance2Params, Seedance2ReferenceInputMode, Seedance2Resolution } from '#types/types';
import { useKernel } from '@/kernel/Kernel';
import PromptReferenceTagCards, {
  hasPromptReferenceTokens,
} from '@/components/PromptReferenceTagCards';

interface Seedance2ControlPanelProps {
  params: Seedance2Params;
  setModelParams: (
    params: (prevParams: { [model: string]: Seedance2Params }) => { [model: string]: Seedance2Params }
  ) => void;
  diffusionModel: string;
  imageReferences?: LockableFile[];
}

const defaultSeedance2Params: Seedance2Params = {
  prompt: '',
  duration: 5,
  resolution: '720p',
  generate_audio: false,
  reference_input_mode: 'frame',
  video_input_start_ms: null,
  video_input_end_ms: null,
  aspect_ratio: 'auto',
  seed: -1,
};

export const getDefaultSeedance2Params = (): Seedance2Params => ({ ...defaultSeedance2Params });

const RESOLUTION_OPTIONS: Seedance2Resolution[] = ['480p', '720p', '1080p', '4k'];
// Seedance 2.5 and the fast tier are capped at 720p; 2.5 runs takes of up to 30 seconds.
const RESOLUTION_OPTIONS_720P_CAP: Seedance2Resolution[] = ['480p', '720p'];
const MAX_DURATION_2_5 = 30;
const MAX_DURATION_2_0 = 15;
const ASPECT_RATIO_OPTIONS: Seedance2AspectRatio[] = ['auto', '21:9', '16:9', '4:3', '1:1', '3:4', '9:16'];
const REFERENCE_INPUT_MODES: Seedance2ReferenceInputMode[] = ['frame', 'video'];

const Seedance2ControlPanel: React.FC<Seedance2ControlPanelProps> = React.memo(({
  params,
  setModelParams,
  diffusionModel,
  imageReferences = [],
}) => {
  const { kernel } = useKernel();
  const isSeedance25 = diffusionModel === 'seedance-2.5';
  const resolutionOptions = isSeedance25 || diffusionModel === 'seedance-2.0-fast'
    ? RESOLUTION_OPTIONS_720P_CAP
    : RESOLUTION_OPTIONS;
  const maxDuration = isSeedance25 ? MAX_DURATION_2_5 : MAX_DURATION_2_0;
  // A model switch can carry over a resolution the new model does not serve
  // (1080p/4k on 2.5). Show the tier that will actually be sent.
  const resolution: Seedance2Resolution =
    resolutionOptions.includes(params.resolution) ? params.resolution : '720p';
  const hasPromptRefs = useMemo(
    () => hasPromptReferenceTokens(params.prompt),
    [params.prompt],
  );
  const referenceInputMode: Seedance2ReferenceInputMode =
    params.reference_input_mode === 'video' ? 'video' : 'frame';
  const aspectRatio: Seedance2AspectRatio =
    ASPECT_RATIO_OPTIONS.includes(params.aspect_ratio as Seedance2AspectRatio)
      ? params.aspect_ratio as Seedance2AspectRatio
      : 'auto';

  const updatePromptValue = useCallback((nextPrompt: string) => {
    setModelParams(prev => {
      const current = prev[diffusionModel];
      if (!current) {
        console.error(`[Seedance2ControlPanel] updatePromptValue: Missing params for model "${diffusionModel}".`);
        return prev;
      }
      return {
        ...prev,
        [diffusionModel]: {
          ...current,
          prompt: nextPrompt,
        },
      };
    });
    kernel.current?.logUserAction('Setting Changed', {
      setting_name: 'prompt',
      value_length: nextPrompt.length,
      model_name: diffusionModel,
      source_component: 'Seedance2ControlPanel',
    });
  }, [diffusionModel, kernel, setModelParams]);

  const handleParamChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const parsedValue = type === 'number' || type === 'range' ? parseFloat(value) : value;

    setModelParams(prev => {
      const current = prev[diffusionModel];
      if (!current) {
        console.error(`[Seedance2ControlPanel] handleParamChange: Missing params for model "${diffusionModel}".`);
        return prev;
      }
      return {
        ...prev,
        [diffusionModel]: {
          ...current,
          [name]: parsedValue,
        },
      };
    });

    if (name === 'prompt') {
      kernel.current?.logUserAction('Setting Changed', {
        setting_name: name,
        value_length: (parsedValue as string).length,
        model_name: diffusionModel,
        source_component: 'Seedance2ControlPanel',
      });
      return;
    }

    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      value: parsedValue,
      model_name: diffusionModel,
      source_component: 'Seedance2ControlPanel',
    });
  }, [diffusionModel, kernel, setModelParams]);

  const handleSelectChange = useCallback((
    name: keyof Seedance2Params,
    value: string
  ) => {
    setModelParams(prev => {
      const current = prev[diffusionModel];
      if (!current) {
        console.error(`[Seedance2ControlPanel] handleSelectChange: Missing params for model "${diffusionModel}".`);
        return prev;
      }
      return {
        ...prev,
        [diffusionModel]: {
          ...current,
          [name]: value,
        },
      };
    });

    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      value,
      model_name: diffusionModel,
      source_component: 'Seedance2ControlPanel',
    });
  }, [diffusionModel, kernel, setModelParams]);

  const onSwitchGenerateAudio = useCallback(() => {
    const live = kernel.current?.getModelParams()[diffusionModel] as Seedance2Params | undefined;
    const nextValue = !live?.generate_audio;
    setModelParams(prev => {
      const current = prev[diffusionModel];
      if (!current) {
        console.error(`[Seedance2ControlPanel] onSwitchGenerateAudio: Missing params for model "${diffusionModel}".`);
        return prev;
      }
      return {
        ...prev,
        [diffusionModel]: {
          ...current,
          generate_audio: nextValue,
        },
      };
    });

    kernel.current?.logUserAction('Setting Changed', {
      setting_name: 'generate_audio',
      value: nextValue,
      model_name: diffusionModel,
      source_component: 'Seedance2ControlPanel',
    });
  }, [diffusionModel, kernel, setModelParams]);

  const handleResolutionChange = useCallback((value: string) => {
    handleSelectChange('resolution', value);
  }, [handleSelectChange]);

  const handleReferenceInputModeChange = useCallback((value: string) => {
    handleSelectChange('reference_input_mode', value === 'video' ? 'video' : 'frame');
  }, [handleSelectChange]);

  const handleAspectRatioChange = useCallback((value: string) => {
    handleSelectChange(
      'aspect_ratio',
      ASPECT_RATIO_OPTIONS.includes(value as Seedance2AspectRatio)
        ? value
        : 'auto',
    );
  }, [handleSelectChange]);

  const handleVideoInputRangeChange = useCallback((
    name: 'video_input_start_ms' | 'video_input_end_ms',
    value: number | null,
  ) => {
    setModelParams(prev => {
      const current = prev[diffusionModel];
      if (!current) {
        console.error(`[Seedance2ControlPanel] handleVideoInputRangeChange: Missing params for model "${diffusionModel}".`);
        return prev;
      }
      return {
        ...prev,
        [diffusionModel]: {
          ...current,
          [name]: value,
        },
      };
    });

    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      value,
      model_name: diffusionModel,
      source_component: 'Seedance2ControlPanel',
    });
  }, [diffusionModel, kernel, setModelParams]);

  const renderPromptDecoration = useCallback(({ textareaRef }: {
    textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  }) => (
    <PromptReferenceTagCards
      prompt={params.prompt}
      imageReferences={imageReferences}
      onPromptChange={updatePromptValue}
      textareaRef={textareaRef}
      className="pointer-events-none absolute inset-0 p-2 text-white"
    />
  ), [imageReferences, params.prompt, updatePromptValue]);

  return (
    <>
      <div className="mb-4">
        <AutoGrowingTextarea
          label="Prompt"
          placeholder="Describe the video you want to generate..."
          name="prompt"
          value={params.prompt}
          onChange={handleParamChange}
          onEnterPress={kernel.current?.triggerGenerate}
          maxHeight={600}
          labelClassName="block text-lg font-semibold text-white mb-2"
          inlineDecorationRenderer={hasPromptRefs ? renderPromptDecoration : undefined}
        />
      </div>

      <div className="mb-4">
        <RangeControl
          label="Duration"
          name="duration"
          min={-1}
          max={maxDuration}
          step={1}
          value={params.duration}
          onChange={handleParamChange}
          description="Seconds. Use -1 for intelligent duration."
        />
      </div>

      <div className="mb-4">
        <Select
          id="seedance2-resolution-select"
          label="Resolution"
          value={resolution}
          options={resolutionOptions}
          onChange={handleResolutionChange}
        />
      </div>

      <div className="mb-4">
        <Select
          id="seedance2-aspect-ratio-select"
          label="Aspect Ratio"
          value={aspectRatio}
          options={ASPECT_RATIO_OPTIONS}
          onChange={handleAspectRatioChange}
        />
      </div>

      <div className="mb-4">
        <Select
          id="seedance2-reference-input-select"
          label="Reference Input"
          value={referenceInputMode}
          options={REFERENCE_INPUT_MODES}
          onChange={handleReferenceInputModeChange}
        />
      </div>

      {referenceInputMode === 'video' && (
        <VideoInputRangeControls
          className="mb-4"
          startMs={params.video_input_start_ms}
          endMs={params.video_input_end_ms}
          onChange={handleVideoInputRangeChange}
        />
      )}

      <div className="flex items-center space-x-2 mb-4">
        <Switch
          checked={params.generate_audio}
          onChange={onSwitchGenerateAudio}
          labelRight="Generate Audio"
        />
      </div>
    </>
  );
});

Seedance2ControlPanel.displayName = 'Seedance2ControlPanel';

export default Seedance2ControlPanel;
